import {
  type ApiToolCallModel,
  ChatStatus,
  isChatStatusBusy,
  type Message as TMessage,
  type Modality,
  type Thread,
  config,
  useStores,
} from '@littlebird/sdk';
import {
  buildReasoningDisplayItems,
  buildToolCallByStepIdMap,
  computeLastInResponseSequenceIds,
  opensTurn,
  type ReasoningModalityLike,
} from '@littlebird/ui-utils';
import { getRouterContext } from '@tanstack/react-router';
import { format } from 'date-fns/format';
import { findLast } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, {
  type Key,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { VList, type VListHandle } from 'virtua';

import { CompactionBanner } from '@/components/blocks/Message/compaction/CompactionBanner';
import { ImageGenQuotaBadge } from '@/components/blocks/Message/image-gen-quota/ImageGenQuotaBadge';
import {
  filterDisplayableModalities,
  ReasoningPanel,
  ReasoningStatus,
  type ReasoningStep,
  ReasoningStepType,
  ThinkingIndicator,
} from '@/components/blocks/Message/reasoning';
import { TurnActivityPanel } from '@/components/blocks/Message/turn-activity';
import { TurnCapHitBanner } from '@/components/blocks/Message/turn-cap-hit/TurnCapHitBanner';
import { Text, XStack } from '@/components/ui';
import { FindOverlay } from '@/components/ui/FindOverlay';
import { Separator } from '@/components/ui/Separator';
import { EntityReferenceResolutionContext } from '@/components/ui/smoothdown/smoothdown-render-context';
import { CanvasActionCardPlacementProvider } from '@/features/action-cards/CanvasActionCardPlacementProvider';
import { MessageActionCards } from '@/features/action-cards/components/MessageActionCards';
import { SignInConflictCard } from '@/features/browser-stream/sign-in-conflict-card';
import { parseSignInConflict } from '@/features/browser-stream/sign-in-session.query';
import { onChatSendFromAnyWindow } from '@/features/chat/chat.events';
import { useIsAssistantResponding } from '@/features/chat/chat.hooks';
import {
  chatStore,
  normalizeThreadCompactionBadgeStates,
  type ThreadCompactionBadgeState,
  type ThreadImageGenQuotaBadgeState,
  type ThreadTurnCapHitState,
} from '@/features/chat/chat.store';
import { hivemindGroupAnswerIds } from '@/features/hivemind';
import { useProactiveAgentsQuery } from '@/features/settings/proactive-agent.query';
import { tabStore } from '@/features/tabs';
import { useSearchFocusState } from '@/hooks/useSearchFocusState';
import { afterFrames, runFrames } from '@/lib/animate';
import { uiStore } from '@/stores/ui.store';
import { userStore } from '@/stores/user.store';
import {
  isChatRenderDebugEnabled,
  logChatRenderDebug,
} from '@/utils/chat-render-debug';
import { classList } from '@/utils/cn';
import { isSystemNote } from '@/utils/system-note';

import Message from '../Message';

import { ChatScrollToLastMessageButton } from './ChatScrollToLastMessageButton';
import { FastScrollBar } from './FastScrollBar';
import { excludeGroupAnswersFromLiveTurn } from './messageListLiveTurn';
import {
  chatScrollAnchorKey as buildChatScrollAnchorKey,
  clampScrollAnchorDelta,
  readScrollAnchor,
  writeScrollAnchor,
  type ScrollAnchor,
} from './scrollAnchor';
import { StreamingLoader } from './StreamingLoader';
import { collapseTurnActivity, omitFirstTurnActivity } from './turnActivity';
import { chatFindTunnel, useChatFind } from './useChatFind';
import {
  BOTTOM_SCROLL_TRACKING_ANCHOR_ID,
  CHAT_THREAD_ID,
  useChatScroll,
} from './useChatScroll';
import { useChatState } from './useChatState';
import { useLiveTurnRegion } from './useLiveTurnRegion';
import { useSplitThread } from './useSplitThread';

import type {
  ChatItem,
  CompactionBannerItem,
  ImageGenQuotaBadgeItem,
  TurnActivityChildItem,
  TurnCapHitBannerItem,
} from './chatItems.types';

/**
 * Number of messages to display initially when a thread loads.
 * Additional messages are lazy-loaded as the user scrolls up.
 */
const INITIAL_MESSAGE_COUNT = 10;
const REVEAL_SETTLE_FRAMES = 60;
const EMPTY_GROUP_ANSWER_IDS: ReadonlySet<string> = new Set();
const WINDOW_EXPAND_SETTLE_FRAMES = 30;
const POST_FULL_LOAD_SETTLE_FRAMES = 2;

async function awaitInFlightOlderLoad(
  ref: React.MutableRefObject<Promise<void> | null>
): Promise<boolean> {
  if (ref.current) {
    await ref.current;
    return true;
  }
  return false;
}

interface MessageListProps {
  messages?: TMessage[];
  threadId?: string;
  height: number;
  previousTurnPreview?: boolean;
  title?: string;
  hideFirstTurnActivity?: boolean;
  wrapFirstMessage?: (message: React.ReactNode) => React.ReactNode;
  afterFirstResponse?: React.ReactNode;
  onAskUserComplete?: () => void;
}

interface ListProps {
  messages: TMessage[];
  height: number;
  threadId?: string;
  thread?: Thread;
  resolveEntityReferences?: boolean;
  isCompact?: boolean;
  compactHorizontalPadding?: boolean;
  previousTurnPreview?: boolean;
  hasOlderMessages?: boolean;
  hideFirstTurnActivity?: boolean;
  wrapFirstMessage?: (message: React.ReactNode) => React.ReactNode;
  afterFirstResponse?: React.ReactNode;
  onAskUserComplete?: () => void;
}

const MAX_HEAVY_CHAT_ITEMS_KEEP_MOUNTED = 16;

function getCopyableBlockCost(content: string) {
  let cost = 0;
  const copyableBlockRegex = /<copyable\b[^>]*>([\s\S]*?)(?:<\/copyable>|$)/gi;
  for (const match of content.matchAll(copyableBlockRegex)) {
    cost += 8_000;
    cost += (match[1]?.length ?? 0) * 4;
  }
  return cost;
}

function getMarkdownTableCost(content: string) {
  const lines = content.split(/\r?\n/);
  let cost = 0;

  for (let index = 0; index < lines.length - 1; index += 1) {
    const line = lines[index];
    const nextLine = lines[index + 1];
    if (!line?.includes('|') || !nextLine?.includes('|')) continue;
    if (!/^\s*\|?\s*:?-{3,}:?/.test(nextLine)) continue;

    let tableLength = line.length + nextLine.length;
    let rowCount = 2;
    index += 2;

    while (index < lines.length && lines[index]?.includes('|')) {
      tableLength += lines[index]?.length ?? 0;
      rowCount += 1;
      index += 1;
    }

    cost += 6_000 + tableLength * 3 + rowCount * 500;
  }

  return cost;
}

function getFencedCodeCost(content: string) {
  let cost = 0;
  const fencedCodeRegex = /```([\w-]*)\n([\s\S]*?)(?:\n```|$)/g;
  for (const match of content.matchAll(fencedCodeRegex)) {
    const language = match[1]?.toLowerCase() ?? '';
    const code = match[2] ?? '';
    const isMermaid = language.startsWith('mer');

    cost += isMermaid ? 8_000 : 2_500;
    cost += code.length * (isMermaid ? 3 : 1);
  }
  return cost;
}

function getMathCost(content: string) {
  const blockMatches = content.match(/\$\$[\s\S]*?\$\$/g) ?? [];
  const inlineMatches = content.match(/(^|[^$])\$[^$\n]+\$/g) ?? [];

  return (
    blockMatches.reduce((sum, match) => sum + 2_500 + match.length * 1.5, 0) +
    inlineMatches.reduce((sum, match) => sum + match.length * 0.75, 0)
  );
}

function getChatItemRenderCost(item: ChatItem) {
  if (item.type !== 'assistantResponse' && item.type !== 'userMessage') {
    return 0;
  }

  const content = item.message.content ?? '';
  return (
    content.length * 0.1 +
    getCopyableBlockCost(content) +
    getMarkdownTableCost(content) +
    getFencedCodeCost(content) +
    getMathCost(content)
  );
}

interface IndexedChatItem {
  item: ChatItem;
  absoluteIndex: number;
  isLive: boolean;
}

/** A VList row as the shift guard sees it. Structural rows (date separators,
 * the older-messages loader) are not evidence of a message that stayed put. */
export interface RowIdentity {
  key: Key;
  isStructural: boolean;
}

/**
 * True when rows only changed at the tail: every row that survived kept its
 * index, and everything after the shared prefix was removed or newly added.
 * This covers appends, tail removals, and tail replacements (a thinking row
 * giving way to activity and assistant rows). A prepend or window shift moves
 * the old rows past the prefix, so their keys reappear in the next tail.
 *
 * The shared prefix must hold at least one message row. A date separator keeps
 * its key across a same-day window replacement (a tail re-bound onto messages
 * that were never on screen), and with nothing else surviving there is no
 * evidence the front held still — such commits need shift compensation.
 *
 * The playground rig at `routes/_authenticated/playground/vlist-overlap` keeps
 * its own copy so it stays self-contained; change both together.
 */
export function isTailOnlyChange(
  previous: RowIdentity[],
  next: RowIdentity[]
): boolean {
  let prefixLength = 0;
  let prefixHasMessageRow = false;
  while (
    prefixLength < previous.length &&
    prefixLength < next.length &&
    previous[prefixLength].key === next[prefixLength].key
  ) {
    prefixHasMessageRow ||= !previous[prefixLength].isStructural;
    prefixLength += 1;
  }
  if (!prefixHasMessageRow) return false;
  const nextTailKeys = new Set(next.slice(prefixLength).map((row) => row.key));
  return previous
    .slice(prefixLength)
    .every((row) => !nextTailKeys.has(row.key));
}

export const List = observer(
  ({
    messages,
    height,
    threadId,
    thread,
    resolveEntityReferences = false,
    isCompact = false,
    compactHorizontalPadding = false,
    previousTurnPreview = false,
    hasOlderMessages = false,
    hideFirstTurnActivity = false,
    wrapFirstMessage,
    afterFirstResponse,
    onAskUserComplete,
  }: ListProps) => {
    const threadContainerRef = useRef<HTMLDivElement>(null);
    const vlistRef = useRef<VListHandle>(null);
    const overscan = 9;
    const [isShifting, setIsShifting] = useState(false);
    const shiftOwnersRef = useRef(new Set<symbol>());
    const startShift = useCallback(() => {
      const owner = Symbol();
      shiftOwnersRef.current.add(owner);
      if (shiftOwnersRef.current.size === 1) setIsShifting(true);
      return owner;
    }, []);
    const endShift = useCallback((owner: symbol) => {
      if (!shiftOwnersRef.current.delete(owner)) return;
      if (shiftOwnersRef.current.size === 0) setIsShifting(false);
    }, []);
    const [isReady, setIsReady] = useState(false);
    const [hasScrolledFromTop, setHasScrolledFromTop] = useState(false);
    const [isLoadingOlder, setIsLoadingOlder] = useState(false);
    const applyPendingRestoreScrollRef = useRef<() => void>(() => {});
    const olderMessagesLoadPromiseRef = useRef<Promise<void> | null>(null);
    const { messages: messagesStore, actionCards } = useStores();
    const router = useContext(getRouterContext());
    const isSharedChat = Boolean(
      router?.matchRoute({ to: '/shared-chat/$id' })
    );
    const answersInTables =
      threadId && !isSharedChat
        ? hivemindGroupAnswerIds(
            messagesStore.getMessagesByThreadId(threadId),
            actionCards.getByChatId(threadId)
          )
        : EMPTY_GROUP_ANSWER_IDS;
    const messagesForLiveTurn = excludeGroupAnswersFromLiveTurn(
      messages,
      answersInTables
    );

    const activeTabId = tabStore.activeTabId ?? 'default-tab';
    const scrollRestorationId = `chat-scroll-${activeTabId}-${threadId ?? 'new'}`;
    const chatWidth = uiStore.chatWidth;

    const { isBusy } = useChatState(thread);
    const isResponding = useIsAssistantResponding(threadId);
    const { data: proactiveAgents } = useProactiveAgentsQuery();
    // A proactive agent's chat shows the notes that open its turns, because
    // the server starts most turns there
    const showSystemNotes =
      proactiveAgents?.instances.some(
        (instance) => instance.chat_id === threadId
      ) ?? false;
    const { searchFocus } = useSearchFocusState(
      threadId ? { threadId } : undefined
    );
    const skipInitialScrollRestore = Boolean(searchFocus.focusQuery);

    // Pixel offsets restored against estimated sizes land on the wrong
    // content; an item anchor stays exact.
    const chatScrollAnchorKey = buildChatScrollAnchorKey(scrollRestorationId);
    const anchorSaveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    // The key rides with the anchor so a flush after a thread change still
    // writes to the thread the position belongs to.
    const lastComputedAnchorRef = useRef<{
      key: string;
      anchor: ScrollAnchor;
    } | null>(null);
    const persistLastAnchor = () => {
      const entry = lastComputedAnchorRef.current;
      if (!entry) return;
      writeScrollAnchor(entry.key, entry.anchor);
    };
    const persistLastAnchorRef = useRef(persistLastAnchor);
    persistLastAnchorRef.current = persistLastAnchor;
    const pendingRestoreAnchorRef = useRef<ScrollAnchor | null>(null);
    // Frozen per thread so the derived window doesn't flip to the tail
    // when the pending anchor clears after the restore settles.
    const windowAnchorMessageIdRef = useRef<string | null>(null);
    const restoreAttemptsRef = useRef(0);
    // Veil the list on restore-opens until the anchor position verifies, so
    // the window top never flashes before the landing.
    const [isRestoring, setIsRestoring] = useState(false);
    // Unkeyed surfaces (Hummingbird) must re-read their anchor per thread.
    // In render on purpose: key-guarded idempotent; an effect paints late.
    const [anchorRecovery, setAnchorRecovery] = useState<ScrollAnchor | null>(
      null
    );
    const restoreKeyRef = useRef<string | null>(null);
    if (restoreKeyRef.current !== scrollRestorationId) {
      restoreKeyRef.current = scrollRestorationId;
      setAnchorRecovery(null);
      pendingRestoreAnchorRef.current = skipInitialScrollRestore
        ? null
        : readScrollAnchor(chatScrollAnchorKey);
      windowAnchorMessageIdRef.current =
        pendingRestoreAnchorRef.current?.messageId ?? null;
      restoreAttemptsRef.current = 0;
      setIsRestoring(Boolean(pendingRestoreAnchorRef.current));
    }

    useEffect(() => {
      if (!isRestoring) return;
      // Recovery paging holds the veil longer; the cap only guards sticking.
      const timer = setTimeout(
        () => setIsRestoring(false),
        anchorRecovery ? 2500 : 600
      );
      return () => clearTimeout(timer);
    }, [isRestoring, anchorRecovery]);

    // Flush the latest position on unmount or thread change; the debounced
    // write alone loses the final moments before a quick tab switch.
    useEffect(
      () => () => {
        clearTimeout(anchorSaveTimerRef.current);
        persistLastAnchorRef.current();
        lastComputedAnchorRef.current = null;
      },
      [chatScrollAnchorKey]
    );

    const {
      thread: messagesToShow,
      threadStartIndex,
      isFullyLoaded,
      isTailWindow,
      expandUp,
      expandDown,
      extendToTail,
      windowTail,
      windowAroundMessage,
      loadFully,
    } = useSplitThread(messages, {
      count: INITIAL_MESSAGE_COUNT,
      resetKey: threadId ?? '',
      anchorMessageId: windowAnchorMessageIdRef.current,
      onSplit(splitMessages) {
        // The window already renders around a restore anchor; no jump needed.
        const anchor = pendingRestoreAnchorRef.current;
        const anchorInWindow = anchor
          ? splitMessages.some((message) => message.id === anchor.messageId)
          : false;
        if (anchorInWindow) {
          setIsReady(true);
          afterFrames(2, () => applyPendingRestoreScrollRef.current());
          return;
        }
        // Missing from memory: older pages may still hold it (only the
        // latest page is loaded), so recover in the background. The veil
        // stays up (600ms cap) so a fast recovery never flashes the tail.
        if (anchor) {
          pendingRestoreAnchorRef.current = null;
          setAnchorRecovery(anchor);
        }

        runFrames(
          10,
          () => {
            if (!vlistRef.current) return;

            // Ensure the latest message is shown when no saved scroll exists
            const chatItems = buildChatItems(splitMessages, undefined, {
              answersInTables,
            });
            logChatRenderDebug('split-jump-to-end', {
              itemCount: chatItems.length,
              offset: Math.round(vlistRef.current.scrollOffset),
            });
            vlistRef.current.scrollToIndex(chatItems.length, {
              align: 'end',
            });
          },
          () => {
            setIsReady(true);
          }
        );
      },
    });

    // Construct chat items responsively.
    // Since List is an observer, this runs whenever accessed observables change.
    // We avoid useMemo here to ensure deep property changes (like thinkingContent)
    // trigger a re-render and re-calculation.
    const compactionBanners =
      threadId && userStore.isInternalUser
        ? normalizeThreadCompactionBadgeStates(
            chatStore.compactionByThread.get(threadId)
          )
        : undefined;
    const turnCapHits = threadId
      ? (chatStore.turnCapHitsByThread.get(threadId) ?? [])
      : undefined;
    const imageGenQuotaBadges = threadId
      ? (chatStore.imageGenQuotaBadgesByThread.get(threadId) ?? [])
      : undefined;
    const builtChatItems = collapseTurnActivity(
      buildChatItems(messagesToShow, thread, {
        compactionBanners,
        turnCapHits,
        imageGenQuotaBadges,
        answersInTables,
        startIndex: threadStartIndex,
        showThinking: true,
        showThoughts: config.chat.show_thoughts,
        isTailWindow,
      }),
      isBusy
    );
    const firstMessage = messages[0];
    const isThreadStartVisible = Boolean(
      !hasOlderMessages &&
      firstMessage &&
      messagesToShow[0]?.id === firstMessage.id
    );
    const visibleChatItems =
      hideFirstTurnActivity && isThreadStartVisible
        ? omitFirstTurnActivity(builtChatItems)
        : builtChatItems;
    const firstMessageDateSeparatorId =
      wrapFirstMessage && isThreadStartVisible && firstMessage
        ? `date-${new Date(firstMessage.createdAt).toDateString()}`
        : null;
    const chatItems = firstMessageDateSeparatorId
      ? visibleChatItems.filter(
          (item) =>
            item.type !== 'dateSeparator' ||
            item.id !== firstMessageDateSeparatorId
        )
      : visibleChatItems;
    // The question picker sits in the composer, so the activity that led to
    // it opens in place; otherwise the user answers without the context.
    const hasLiveAskUser = Boolean(
      threadId && messagesStore.getLiveAskUserRequest(threadId)
    );
    const forceExpandedIndex = hasLiveAskUser
      ? findCurrentTurnActivityIndex(chatItems)
      : -1;
    const isAtTailRef = useRef(true);
    const userHasScrolledRef = useRef(false);
    const {
      liveStartIndex,
      isLive,
      isLiveInView,
      liveRegionRef,
      liveBufferRef,
      showLiveBuffer,
    } = useLiveTurnRegion({
      threadId,
      messages: messagesForLiveTurn,
      chatItems,
      isBusy,
      isResponding,
      threadContainerRef,
      isAtTailRef,
      userHasScrolledRef,
    });
    const liveRegionHeight = previousTurnPreview ? '75cqh' : '100cqh';

    const {
      streamingTipInViewportRef,
      bottomAnchorInViewportRef,
      showScrollToBottom,
      scrollToBottom,
      scrollOffset,
    } = useChatScroll(threadId ?? '', isLive, userHasScrolledRef);
    const isAtTail = isTailWindow && !showScrollToBottom;
    useLayoutEffect(() => {
      isAtTailRef.current = isAtTail;
    }, [isAtTail]);

    // Mirror for callbacks that run outside the render; live items are
    // excluded from anchoring (their offsets belong to the footer).
    const listMetaRef = useRef<{
      items: ChatItem[];
      maxAnchorIndex: number;
      windowStart: number;
      isTailWindow: boolean;
    }>({ items: [], maxAnchorIndex: -1, windowStart: 0, isTailWindow: true });
    listMetaRef.current = {
      items: chatItems,
      maxAnchorIndex: (liveStartIndex ?? chatItems.length) - 1,
      windowStart: threadStartIndex,
      isTailWindow,
    };

    // Ref: onSplit above calls it before this hook's results exist. Verify
    // and retry: one scrollToIndex can land off-target while virtua measures.
    applyPendingRestoreScrollRef.current = () => {
      const anchor = pendingRestoreAnchorRef.current ?? null;
      if (anchor === null) return;
      const finishRestore = () => {
        pendingRestoreAnchorRef.current = null;
        setIsRestoring(false);
      };
      // Bail only on user takeover (a wheel or a send that activated the
      // live turn); a thread still streaming in the background restores.
      if (userHasScrolledRef.current || isLive) {
        finishRestore();
        return;
      }
      const findAnchorItemIndex = () =>
        listMetaRef.current.items.findIndex(
          (item) =>
            (item.type === 'userMessage' ||
              item.type === 'assistantResponse') &&
            item.message.id === anchor.messageId
        );
      const vlist = vlistRef.current;
      const anchorItemIndex = findAnchorItemIndex();
      if (!vlist || anchorItemIndex === -1) {
        finishRestore();
        return;
      }
      restoreAttemptsRef.current += 1;
      const restoreDelta = clampScrollAnchorDelta(
        anchor.delta,
        vlist.getItemSize(anchorItemIndex)
      );
      logChatRenderDebug('scroll-restore-applied', {
        anchor,
        anchorItemIndex,
        attempt: restoreAttemptsRef.current,
        restoreDelta,
        windowStart: threadStartIndex,
      });
      vlist.scrollToIndex(anchorItemIndex, {
        align: 'start',
        // Bring an unmeasured row into view so Virtua can measure it. The
        // verification below keeps the restore pending until that happens.
        offset: restoreDelta ?? 0,
      });
      afterFrames(3, () => {
        // A newer restore or a user takeover already resolved this one.
        if (pendingRestoreAnchorRef.current !== anchor) return;
        const vl = vlistRef.current;
        const index = findAnchorItemIndex();
        if (!vl || index === -1) {
          finishRestore();
          return;
        }
        const landedDelta = vl.scrollOffset - vl.getItemOffset(index);
        const expectedDelta = clampScrollAnchorDelta(
          anchor.delta,
          vl.getItemSize(index)
        );
        if (expectedDelta === null) {
          if (restoreAttemptsRef.current >= 5) {
            finishRestore();
            return;
          }
          afterFrames(1, () => applyPendingRestoreScrollRef.current());
          return;
        }
        if (
          Math.abs(landedDelta - expectedDelta) <= 8 ||
          restoreAttemptsRef.current >= 5 ||
          userHasScrolledRef.current
        ) {
          finishRestore();
          return;
        }
        applyPendingRestoreScrollRef.current();
      });
    };

    const indexedChatItems = useMemo<IndexedChatItem[]>(
      () =>
        chatItems.map((item, absoluteIndex) => ({
          item,
          absoluteIndex,
          isLive: liveStartIndex !== null && absoluteIndex >= liveStartIndex,
        })),
      [chatItems, liveStartIndex]
    );

    const liveItems = useMemo(
      () => indexedChatItems.filter((item) => item.isLive),
      [indexedChatItems]
    );
    const firstMessageId =
      wrapFirstMessage && isThreadStartVisible ? firstMessage?.id : undefined;
    const firstResponseEndId =
      afterFirstResponse && isThreadStartVisible
        ? findFirstResponseEndId(chatItems)
        : undefined;

    const renderChatItem = useCallback(
      (
        chatItem: ChatItem,
        index: number,
        keyPrefix: string,
        isLiveItem: boolean
      ) =>
        renderIndexedChatItem({
          chatItem,
          chatWidth,
          index,
          isLive: isLiveItem,
          keyPrefix,
          threadId: threadId ?? '',
          isCompact,
          compactHorizontalPadding,
          forceExpanded: index === forceExpandedIndex,
          firstMessageId,
          showSystemNotes,
          wrapFirstMessage,
          firstResponseEndId,
          afterFirstResponse,
          onAskUserComplete,
        }),
      [
        afterFirstResponse,
        chatWidth,
        compactHorizontalPadding,
        firstMessageId,
        firstResponseEndId,
        forceExpandedIndex,
        isCompact,
        onAskUserComplete,
        showSystemNotes,
        wrapFirstMessage,
        threadId,
      ]
    );

    const footerItem = useMemo(
      () => (
        <div key="chat-thread-footer" className="relative">
          {isLive && (
            <div
              ref={liveRegionRef}
              data-live-turn
              data-previous-turn-preview={previousTurnPreview}
              style={{ minHeight: liveRegionHeight }}
            >
              {liveItems.map(({ item, absoluteIndex }) =>
                renderChatItem(item, absoluteIndex, 'live', true)
              )}
              {/* Tracks the streaming tip — when this is in the viewport, the scroll-to-bottom button stays hidden */}
              <div
                ref={streamingTipInViewportRef}
                className="scroll-tracking-anchor size-2"
              />
              {/* Breathing room below the streaming response; collapses once typing stops and the user has scrolled past it */}
              {showLiveBuffer && (
                <div
                  ref={liveBufferRef}
                  aria-hidden="true"
                  style={{ height: '65cqh' }}
                />
              )}
            </div>
          )}
          {/* Where the scroll-to-bottom button lands when clicked. Also tracks visibility outside a live turn */}
          <div
            key={BOTTOM_SCROLL_TRACKING_ANCHOR_ID}
            id={BOTTOM_SCROLL_TRACKING_ANCHOR_ID}
            ref={bottomAnchorInViewportRef}
            className="scroll-tracking-anchor size-2"
          />
        </div>
      ),
      [
        streamingTipInViewportRef,
        bottomAnchorInViewportRef,
        isLive,
        liveItems,
        liveBufferRef,
        showLiveBuffer,
        renderChatItem,
        previousTurnPreview,
        liveRegionHeight,
        liveRegionRef,
      ]
    );

    // Live items render only in the footer — omitting them from VList
    // children avoids ResizeObserver scroll compensation on consecutive sends
    const historicalCount = liveStartIndex ?? indexedChatItems.length;

    const items = useMemo(
      () => [
        ...(isLoadingOlder
          ? [
              <div
                key="older-loader"
                data-find-exclude
                className="flex justify-center py-4"
              >
                <div className="text-xs text-foreground-muted animate-pulse">
                  Loading older messages...
                </div>
              </div>,
            ]
          : []),
        ...indexedChatItems
          .slice(0, historicalCount)
          .map(({ item, absoluteIndex, isLive }) =>
            renderChatItem(item, absoluteIndex, 'historical', isLive)
          ),
        footerItem,
      ],
      [
        footerItem,
        indexedChatItems,
        historicalCount,
        isLoadingOlder,
        renderChatItem,
      ]
    );

    // Virtua reads `shift` only when the row count changes and treats the
    // whole delta as a front insert/removal: it unshifts the new entries into
    // its size cache and moves every existing cached size to index+N. A tail
    // change committed inside the settle window (a live turn migrating into
    // the list, a background turn streaming rows) would leave every row that
    // stays mounted with a neighbour's cached height — rows after a tall
    // message are then laid out over it. Drop shift for the commit whose only
    // change is a tail append/removal of the rows before the footer. Rows
    // mirror the VList children minus the footer; the date separator and the
    // loader are structural, since a separator keeps its key across a same-day
    // prepend or window replacement and so proves nothing about the front.
    const committedRowsRef = useRef<RowIdentity[] | null>(null);
    const rows = useMemo<RowIdentity[]>(
      () => [
        ...(isLoadingOlder
          ? [{ key: 'older-loader', isStructural: true }]
          : []),
        ...indexedChatItems.slice(0, historicalCount).map(({ item }) => ({
          key: 'message' in item ? item.message.id : item.id,
          isStructural: item.type === 'dateSeparator',
        })),
      ],
      [historicalCount, indexedChatItems, isLoadingOlder]
    );
    const committedRows = committedRowsRef.current;
    const isTailOnlyRowChange =
      committedRows !== null &&
      committedRows.length !== rows.length &&
      isTailOnlyChange(committedRows, rows);
    useLayoutEffect(() => {
      committedRowsRef.current = rows;
    });

    // Tail re-bounds drop everything above the new window, so they must run
    // under shift compensation like expandUpWithShift — otherwise the browser
    // clamps scrollTop and a frame paints in the wrong place before the live
    // region pins to the bottom.
    const windowTailWithShift = useCallback(() => {
      // A tail window too short to fill its viewport would expand straight
      // back up, and a frame could paint without the rows above the new window
      const vlist = vlistRef.current;
      if (isTailWindow && vlist && vlist.scrollSize <= vlist.viewportSize)
        return false;
      const shiftOwner = startShift();
      if (!windowTail()) {
        endShift(shiftOwner);
        return false;
      }
      logChatRenderDebug('window-tail', {});
      afterFrames(WINDOW_EXPAND_SETTLE_FRAMES, () => endShift(shiftOwner));
      return true;
    }, [endShift, isTailWindow, startShift, windowTail]);

    const handleScrollToBottom = useCallback(() => {
      // A fresh bounded tail window, not a widen: widening from a deep
      // mid-history window rebuilds items for the whole suffix.
      const rebounded = windowTailWithShift();
      const shouldUseInstantJump = isBusy && isLiveInView;
      if (rebounded) {
        // Let the new window commit so the jump measures fresh geometry.
        afterFrames(2, () => scrollToBottom(false));
        return;
      }
      scrollToBottom(!shouldUseInstantJump);
    }, [windowTailWithShift, isBusy, isLiveInView, scrollToBottom]);

    // Prepends must run under shift compensation, one expansion at a time.
    const expandUpInFlightRef = useRef(false);
    const expandUpWithShift = useCallback(() => {
      if (expandUpInFlightRef.current) return;
      expandUpInFlightRef.current = true;
      const shiftOwner = startShift();
      if (!expandUp()) {
        endShift(shiftOwner);
        expandUpInFlightRef.current = false;
        return;
      }
      logChatRenderDebug('window-expand-up', {});
      afterFrames(WINDOW_EXPAND_SETTLE_FRAMES, () => {
        endShift(shiftOwner);
        expandUpInFlightRef.current = false;
      });
    }, [endShift, expandUp, startShift]);

    const keepMounted = useMemo(() => {
      const startedAt = performance.now();
      const loaderOffset = isLoadingOlder ? 1 : 0;
      const footerIndex = historicalCount + (isLoadingOlder ? 1 : 0);
      let scoredItemCount = 0;
      const scoredItems = indexedChatItems
        .slice(0, historicalCount)
        .map(({ item, absoluteIndex }) => {
          const renderCost = getChatItemRenderCost(item);
          if (renderCost > 0) scoredItemCount += 1;
          return {
            index: absoluteIndex + loaderOffset,
            renderCost,
          };
        })
        .filter(({ renderCost }) => renderCost > 0);
      const heaviestHistoricalIndexes = scoredItems
        .sort((a, b) => b.renderCost - a.renderCost)
        .slice(0, MAX_HEAVY_CHAT_ITEMS_KEEP_MOUNTED)
        .map(({ index }) => index);

      const keep = [...heaviestHistoricalIndexes, footerIndex];
      if (historicalCount > 0) keep.push(footerIndex - 1);
      const keepMountedIndexes = Array.from(new Set(keep));

      const elapsedMs = performance.now() - startedAt;
      logChatRenderDebug('keep-mounted-cost', {
        elapsedMs: Number(elapsedMs.toFixed(3)),
        historicalCount,
        isLoadingOlder,
        keptCount: keepMountedIndexes.length,
        maxKeepMounted: MAX_HEAVY_CHAT_ITEMS_KEEP_MOUNTED,
        scoredItemCount,
        topCosts: scoredItems.slice(0, 8).map(({ index, renderCost }) => ({
          index,
          renderCost: Math.round(renderCost),
        })),
      });

      return keepMountedIndexes;
    }, [historicalCount, indexedChatItems, isLoadingOlder]);

    // Guards double-triggering: window ops re-create the callbacks below
    // and re-run the effects that call this.
    const revealPromiseRef = useRef<{
      key: string;
      promise: Promise<void>;
    } | null>(null);
    const revealInMemoryMessages = useCallback(() => {
      const key = threadId ?? '';
      if (revealPromiseRef.current?.key === key) {
        return revealPromiseRef.current.promise;
      }
      if (isFullyLoaded) return Promise.resolve();
      const promise = new Promise<void>((resolve) => {
        logChatRenderDebug('load-fully-start', {
          threadId,
          scrollSize: Math.round(vlistRef.current?.scrollSize ?? -1),
        });
        // Append the tail first (appends don't move content), then prepend
        // the rest under shift compensation.
        extendToTail();
        afterFrames(2, () => {
          const shiftOwner = startShift();
          loadFully();
          afterFrames(REVEAL_SETTLE_FRAMES, () => {
            endShift(shiftOwner);
            logChatRenderDebug('load-fully-end', {
              threadId,
              scrollSize: Math.round(vlistRef.current?.scrollSize ?? -1),
            });
            resolve();
          });
        });
      });
      revealPromiseRef.current = { key, promise };
      return promise;
    }, [
      endShift,
      extendToTail,
      isFullyLoaded,
      loadFully,
      startShift,
      threadId,
    ]);

    const loadOlderFromStorage = useCallback(async () => {
      if (!threadId || !messagesStore.hasOlderMessages(threadId)) return;
      if (await awaitInFlightOlderLoad(olderMessagesLoadPromiseRef)) return;

      setIsLoadingOlder(true);
      const loadPromise = messagesStore
        .loadOlderMessages(threadId)
        .then(() => undefined)
        .finally(() => {
          olderMessagesLoadPromiseRef.current = null;
          setIsLoadingOlder(false);
        });

      olderMessagesLoadPromiseRef.current = loadPromise;
      await loadPromise;
    }, [messagesStore, threadId]);

    const loadAllOlderFromStorage = useCallback(async () => {
      if (!threadId) return;

      await awaitInFlightOlderLoad(olderMessagesLoadPromiseRef);

      if (!messagesStore.hasOlderMessages(threadId)) return;

      setIsLoadingOlder(true);
      const loadPromise = (async () => {
        try {
          while (messagesStore.hasOlderMessages(threadId)) {
            const page = await messagesStore.loadOlderMessages(threadId);
            if (!page.hasMore || page.records.length === 0) break;
          }
        } finally {
          olderMessagesLoadPromiseRef.current = null;
          setIsLoadingOlder(false);
        }
      })();

      olderMessagesLoadPromiseRef.current = loadPromise;
      await loadPromise;
    }, [messagesStore, threadId]);

    // Page older history for an anchor whose message was evicted with the
    // thread; only exhausted pages mean it is genuinely gone.
    useEffect(() => {
      if (!anchorRecovery || !threadId) return;
      let cancelled = false;
      void (async () => {
        // Serialised through loadOlderFromStorage's single flight; stops
        // when pages are exhausted, the user scrolls, or the thread changes.
        for (;;) {
          if (cancelled) return;
          if (userHasScrolledRef.current) {
            setIsRestoring(false);
            return;
          }
          const known = messagesStore
            .getMessagesByThreadId(threadId)
            .some((message) => message.id === anchorRecovery.messageId);
          if (known) {
            // The messages prop lags the store by a render; retry the
            // re-window across frames until the ref catches up.
            for (let attempt = 0; attempt < 30 && !cancelled; attempt += 1) {
              if (windowAroundMessage(anchorRecovery.messageId)) {
                pendingRestoreAnchorRef.current = anchorRecovery;
                setIsRestoring(true);
                afterFrames(2, () => applyPendingRestoreScrollRef.current());
                return;
              }
              await new Promise<void>((resolve) =>
                afterFrames(1, () => resolve())
              );
            }
            setIsRestoring(false);
            return;
          }
          if (!messagesStore.hasOlderMessages(threadId)) {
            setIsRestoring(false);
            return;
          }
          await loadOlderFromStorage();
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [
      anchorRecovery,
      loadOlderFromStorage,
      messagesStore,
      threadId,
      userHasScrolledRef,
      windowAroundMessage,
    ]);

    // The live turn renders from the thread tail; keep it in the window.
    useEffect(() => {
      if (!threadId) return;
      const listener = onChatSendFromAnyWindow((sentThreadId) => {
        if (sentThreadId !== threadId) return;
        windowTailWithShift();
      });
      return () => listener.unsubscribe();
    }, [threadId, windowTailWithShift]);

    // Virtua clamps underfilled scroll size to the viewport; equality must expand
    useEffect(() => {
      if (!isReady || isShifting) return;
      let invalidated = false;
      afterFrames(3, () => {
        if (invalidated) return;
        const vlist = vlistRef.current;
        if (!vlist || vlist.scrollSize > vlist.viewportSize) return;
        if (!expandDown() && isTailWindow) expandUpWithShift();
      });
      return () => {
        invalidated = true;
      };
    }, [
      isReady,
      isShifting,
      isTailWindow,
      expandDown,
      expandUpWithShift,
      messages.length,
      messagesToShow.length,
    ]);

    const ensureThreadFullyLoaded = useCallback(
      async (onReady?: () => void) => {
        await revealInMemoryMessages();
        await loadAllOlderFromStorage();
        await new Promise<void>((resolve) => {
          afterFrames(POST_FULL_LOAD_SETTLE_FRAMES, () => {
            onReady?.();
            resolve();
          });
        });
      },
      [loadAllOlderFromStorage, revealInMemoryMessages]
    );

    const find = useChatFind({
      messages,
      chatItems,
      vlistRef,
      containerRef: threadContainerRef,
      threadId,
    });
    const {
      repaintHighlights,
      hasPendingSearchFocus,
      focusSearchTarget,
      isOpen: isFindOpen,
    } = find;

    const handleOnScroll = useCallback(
      (offset: number) => {
        const scrolledFromTop = offset > 0;
        if (scrolledFromTop !== hasScrolledFromTop) {
          setHasScrolledFromTop(scrolledFromTop);
        }
        const delta = offset - scrollOffset.current;
        if (Math.abs(delta) > 150) {
          logChatRenderDebug('scroll-jump', {
            from: Math.round(scrollOffset.current),
            to: Math.round(offset),
            delta: Math.round(delta),
            scrollSize: Math.round(vlistRef.current?.scrollSize ?? -1),
          });
        }
        scrollOffset.current = offset;

        const vlist = vlistRef.current;
        const viewport = vlist?.viewportSize ?? 0;
        const { windowStart, isTailWindow: tailWindow } = listMetaRef.current;

        // Near the window top: expand in-memory history first, page storage
        // at the true start. A settling restore owns the scroll until then.
        if (offset <= viewport && !pendingRestoreAnchorRef.current) {
          if (windowStart > 0) {
            expandUpWithShift();
          } else if (offset <= 0) {
            logChatRenderDebug('load-older-storage-trigger', {
              offset: Math.round(offset),
            });
            void loadOlderFromStorage();
          }
        }

        // Approaching the bottom of a mid-history window: reveal more below.
        if (
          !tailWindow &&
          vlist &&
          vlist.scrollSize - viewport - offset <= viewport
        ) {
          if (expandDown()) logChatRenderDebug('window-expand-down', {});
        }

        // The loader row shifts VList indexes and a settling restore's
        // programmatic scrolls would overwrite the anchor; block saving.
        if (!isLoadingOlder && !pendingRestoreAnchorRef.current) {
          // Computed per event so the unmount flush has the latest
          // position; only the persistence is debounced.
          const vl = vlistRef.current;
          if (vl) {
            const { items, maxAnchorIndex } = listMetaRef.current;
            // Anchor to the nearest message at or above the viewport top; at
            // the very top only separators remain, so take the one below.
            const findMessageItem = (from: number, direction: -1 | 1) => {
              for (
                let i = from;
                i >= 0 && i <= maxAnchorIndex;
                i += direction
              ) {
                const item = items[i];
                if (
                  item?.type === 'userMessage' ||
                  item?.type === 'assistantResponse'
                ) {
                  return { item, index: i };
                }
              }
              return null;
            };
            const startIndex = Math.min(vl.findStartIndex(), maxAnchorIndex);
            const found =
              findMessageItem(startIndex, -1) ??
              findMessageItem(startIndex + 1, 1);
            if (found) {
              const anchorDelta =
                vl.scrollOffset - vl.getItemOffset(found.index);
              lastComputedAnchorRef.current = {
                key: chatScrollAnchorKey,
                anchor: {
                  messageId: found.item.message.id,
                  delta: Math.round(anchorDelta),
                },
              };
              clearTimeout(anchorSaveTimerRef.current);
              anchorSaveTimerRef.current = setTimeout(
                () => persistLastAnchorRef.current(),
                300
              );
            }
          }
        }

        repaintHighlights();
      },
      [
        chatScrollAnchorKey,
        expandDown,
        hasScrolledFromTop,
        expandUpWithShift,
        isLoadingOlder,
        loadOlderFromStorage,
        repaintHighlights,
        scrollOffset,
      ]
    );

    useEffect(() => {
      if (!isReady) return;
      const scrolledFromTop = (vlistRef.current?.scrollOffset ?? 0) > 0;
      if (scrolledFromTop !== hasScrolledFromTop) {
        setHasScrolledFromTop(scrolledFromTop);
      }
    }, [hasScrolledFromTop, isReady, threadId]);

    useEffect(() => {
      if (!isFindOpen) return;
      void ensureThreadFullyLoaded();
    }, [isFindOpen, ensureThreadFullyLoaded]);

    useEffect(() => {
      if (!hasPendingSearchFocus || !isReady) return;
      void ensureThreadFullyLoaded(() => {
        focusSearchTarget();
      });
    }, [
      ensureThreadFullyLoaded,
      focusSearchTarget,
      hasPendingSearchFocus,
      isReady,
    ]);

    const handleScrollToIndex = useCallback((index: number) => {
      // Use offset to account for the top fade/mask on the container
      vlistRef.current?.scrollToIndex(index, {
        align: 'start',
        smooth: true,
        offset: -16,
      });
    }, []);

    // Visible range in full message-index space, for the fast-scroll bar.
    const getVisibleRange = useCallback(() => {
      const vlist = vlistRef.current;
      const { items, windowStart } = listMetaRef.current;
      if (!vlist || !items.length) return { start: 0, end: 0 };
      const toMessageIndex = (visibleIndex: number, direction: 1 | -1) => {
        let i = Math.max(0, Math.min(visibleIndex, items.length - 1));
        for (; i >= 0 && i < items.length; i += direction) {
          const item = items[i];
          if (
            item.type === 'userMessage' ||
            item.type === 'assistantResponse'
          ) {
            return item.index;
          }
        }
        return windowStart;
      };
      return {
        start: toMessageIndex(vlist.findStartIndex(), 1),
        end: toMessageIndex(vlist.findEndIndex(), -1),
      };
    }, []);

    const getScrollBounds = useCallback(() => {
      const currentScrollOffset = vlistRef.current?.scrollOffset ?? 0;
      const scrollSize = vlistRef.current?.scrollSize ?? 0;
      const viewportSize = vlistRef.current?.viewportSize ?? 0;
      const maxScroll = scrollSize - viewportSize;
      const { windowStart, isTailWindow: tailWindow } = listMetaRef.current;
      return {
        atTop: windowStart === 0 && currentScrollOffset <= 10,
        atBottom: tailWindow && currentScrollOffset >= maxScroll - 10,
      };
    }, []);

    // Fast-scroll jumps re-window around out-of-window targets, the same
    // operation as a scroll restore.
    const handleJumpToMessage = useCallback(
      (messageId: string) => {
        const findItemIndex = () =>
          listMetaRef.current.items.findIndex(
            (item) =>
              (item.type === 'userMessage' ||
                item.type === 'assistantResponse') &&
              item.message.id === messageId
          );
        const inWindow = findItemIndex();
        if (inWindow !== -1) {
          handleScrollToIndex(inWindow);
          return;
        }
        if (!windowAroundMessage(messageId)) return;
        logChatRenderDebug('window-jump', { messageId });
        afterFrames(2, () => {
          const index = findItemIndex();
          if (index === -1) return;
          // Use offset to account for the top fade/mask on the container
          vlistRef.current?.scrollToIndex(index, {
            align: 'start',
            offset: -16,
          });
        });
      },
      [handleScrollToIndex, windowAroundMessage]
    );

    if (!thread) return null;

    return (
      <EntityReferenceResolutionContext.Provider
        value={resolveEntityReferences}
      >
        <div
          ref={threadContainerRef}
          className={classList(
            'relative w-full',
            hasScrolledFromTop &&
              'mask-linear mask-from-0 mask-via-100 mask-to-100 mask-point-via-[1rem]'
          )}
          style={{ height: `${height}px` }}
          data-compact={isCompact}
        >
          <chatFindTunnel.In>
            <FindOverlay
              isOpen={find.isOpen}
              query={find.query}
              onQueryChange={find.setQuery}
              caseSensitive={find.caseSensitive}
              onCaseSensitiveChange={find.setCaseSensitive}
              currentMatch={find.currentMatch}
              totalMatches={find.totalMatches}
              onNext={find.goToNext}
              onPrevious={find.goToPrevious}
              onClose={find.close}
            />
          </chatFindTunnel.In>
          <FastScrollBar
            messages={messages}
            onJumpToMessage={handleJumpToMessage}
            show={isReady}
            getVisibleRange={getVisibleRange}
            getScrollBounds={getScrollBounds}
            scrollContainerId={`${CHAT_THREAD_ID}-${threadId}`}
          />
          <div
            className={classList(
              'contain-strict px-0',
              isRestoring && 'opacity-0'
            )}
            style={{ height: `${height}px` }}
          >
            {/**
             * Performance Note:
             * We use native browser scrolling (vanilla scroll) in WKWebView for optimal performance.
             * Custom scroll implementations significantly degrade performance in WebKit.
             *
             * Guidelines:
             * - DO NOT replace with custom scroll libraries or implementations
             * - Only add styling to the scrollbar as needed
             * - Maintain the native momentum scrolling behavior
             */}
            <VList
              className={classList(
                'scrollbar-hover',
                'h-chat-thread py-4 no-record',
                '[container-type:size]',
                'overscroll-none',
                'mask-linear mask-from-100 mask-via-100 mask-to-0 mask-point-via-[95%]'
              )}
              id={`${CHAT_THREAD_ID}-${threadId}`}
              ref={vlistRef}
              keepMounted={keepMounted}
              style={{ height: `${height}px`, scrollbarGutter: 'stable' }}
              shift={isShifting && !isTailOnlyRowChange}
              onScroll={handleOnScroll}
              overscan={overscan}
            >
              {items}
            </VList>
          </div>
          <ChatScrollToLastMessageButton
            show={showScrollToBottom && isReady}
            isResponding={isBusy}
            onScrollToBottom={handleScrollToBottom}
          />
          {!isCompact && <StreamingLoader threadId={threadId ?? ''} />}
        </div>
      </EntityReferenceResolutionContext.Provider>
    );
  }
);

const MessageList = observer(
  ({
    messages,
    threadId,
    height,
    previousTurnPreview,
    hideFirstTurnActivity,
    wrapFirstMessage,
    afterFirstResponse,
    onAskUserComplete,
  }: MessageListProps) => {
    const { messages: messagesStore, threads: threadsStore } = useStores();
    const thread = threadId ? threadsStore.getById(threadId) : undefined;
    const fallbackMessages = threadId
      ? messagesStore.getMessagesByThreadId(threadId)
      : [];
    const usedMessages = messages ?? fallbackMessages;
    const usedThread = thread ?? undefined;
    const canvasFileIds = Array.from(
      new Set(
        usedMessages.flatMap((message) =>
          message.role === 'assistant' ? message.fileIds : []
        )
      )
    );
    if (isChatRenderDebugEnabled()) {
      const lastMessage = usedMessages.at(-1);
      logChatRenderDebug(
        'message-list-render',
        {
          threadStatus: usedThread?.status,
          messageCount: usedMessages.length,
          lastMessageLength: lastMessage?.content?.length ?? 0,
        },
        threadId ?? 'unbound'
      );
    }

    return (
      <CanvasActionCardPlacementProvider fileIds={canvasFileIds}>
        <List
          resolveEntityReferences
          messages={usedMessages}
          height={height}
          threadId={threadId}
          thread={usedThread}
          previousTurnPreview={previousTurnPreview}
          hasOlderMessages={
            threadId
              ? messagesStore.hasOlderMessages(threadId) ||
                messagesStore.isThreadHistorySyncing(threadId)
              : false
          }
          hideFirstTurnActivity={hideFirstTurnActivity}
          wrapFirstMessage={wrapFirstMessage}
          afterFirstResponse={afterFirstResponse}
          onAskUserComplete={onAskUserComplete}
        />
      </CanvasActionCardPlacementProvider>
    );
  }
);

export default MessageList;

interface ChatMessageProps {
  chatWidth: string;
  id: string;
  threadId: string;
  index: number;
  message: TMessage;
  isCompact?: boolean;
  isLastAssistantMessage?: boolean;
  isNewestResponse?: boolean;
  isLive?: boolean;
  followsAssistantResponse?: boolean;
  compactHorizontalPadding?: boolean;
  onAskUserComplete?: () => void;
}

const ChatMessage = React.memo(function ChatMessage({
  chatWidth,
  id,
  threadId,
  message,
  index,
  isCompact = false,
  isLastAssistantMessage = false,
  isNewestResponse = false,
  isLive = false,
  followsAssistantResponse = false,
  compactHorizontalPadding = false,
  onAskUserComplete,
}: ChatMessageProps) {
  return (
    <div
      id={id}
      className={classList(
        'mx-auto',
        compactHorizontalPadding
          ? 'w-full px-3'
          : 'w-full px-3 md:px-12 [[data-context-chat]_&]:px-6',
        'transform-gpu',
        followsAssistantResponse ? 'pt-3' : '',
        chatWidth
      )}
    >
      <Message
        isCompact={isCompact}
        allowEdit
        threadId={threadId}
        message={message}
        messageIndex={index}
        isLastAssistantMessage={isLastAssistantMessage}
        isNewestResponse={isNewestResponse}
        isLive={isLive}
        onAskUserComplete={onAskUserComplete}
      />
    </div>
  );
});

const ChatItemWrapper = React.memo(
  ({
    chatWidth,
    children,
    id,
    compactHorizontalPadding,
  }: {
    chatWidth: string;
    children: React.ReactNode;
    id: string;
    compactHorizontalPadding: boolean;
  }) => {
    return (
      <div
        id={id}
        data-find-exclude
        className={classList(
          'mx-auto',
          compactHorizontalPadding
            ? 'w-full px-3'
            : 'w-full px-3 md:px-12 [[data-context-chat]_&]:px-6',
          'transform-gpu',
          chatWidth
        )}
      >
        {children}
      </div>
    );
  }
);

/** The reply that ends the first turn; a later turn's reply never counts */
function findFirstResponseEndId(chatItems: ChatItem[]): string | undefined {
  let turnCount = 0;
  for (const item of chatItems) {
    if (item.type === 'userMessage' && opensTurn(item.message)) {
      turnCount += 1;
      if (turnCount > 1) return undefined;
    }
    if (item.type === 'assistantResponse' && item.isLastInResponseSequence) {
      return item.message.id;
    }
  }
  return undefined;
}

/** The activity row of the turn in progress; -1 once a user message closes it. */
function findCurrentTurnActivityIndex(chatItems: ChatItem[]): number {
  for (let index = chatItems.length - 1; index >= 0; index -= 1) {
    const { type } = chatItems[index];
    if (type === 'userMessage') return -1;
    if (type === 'turnActivity' || type === 'reasoning') return index;
  }
  return -1;
}

function renderTurnActivityChild(
  chatItem: TurnActivityChildItem,
  threadId: string,
  isCompact: boolean,
  forceExpanded: boolean
): React.ReactNode {
  if (chatItem.type === 'reasoning') {
    return (
      <ReasoningPanel
        key={chatItem.id}
        id={chatItem.id}
        steps={chatItem.steps}
        status={chatItem.status}
        isCompact={isCompact}
        forceExpanded={forceExpanded}
      />
    );
  }

  return (
    <Message
      key={chatItem.message.id}
      isCompact={isCompact}
      threadId={threadId}
      message={chatItem.message}
      messageIndex={chatItem.index}
    />
  );
}

function renderIndexedChatItem({
  chatItem,
  chatWidth,
  index,
  isLive,
  keyPrefix,
  threadId,
  isCompact,
  compactHorizontalPadding,
  forceExpanded,
  firstMessageId,
  showSystemNotes,
  wrapFirstMessage,
  firstResponseEndId,
  afterFirstResponse,
  onAskUserComplete,
}: {
  chatItem: ChatItem;
  chatWidth: string;
  index: number;
  isLive: boolean;
  keyPrefix: string;
  threadId: string;
  isCompact: boolean;
  compactHorizontalPadding: boolean;
  forceExpanded: boolean;
  firstMessageId?: string;
  showSystemNotes: boolean;
  wrapFirstMessage?: (message: React.ReactNode) => React.ReactNode;
  firstResponseEndId?: string;
  afterFirstResponse?: React.ReactNode;
  onAskUserComplete?: () => void;
}) {
  if (chatItem.type === 'dateSeparator') {
    return (
      <div id={`chat-item-${index}`} key={`${keyPrefix}-${chatItem.id}`}>
        <DateSeparator date={chatItem.date} />
      </div>
    );
  }

  if (chatItem.type === 'importDivider') {
    return (
      <ChatItemWrapper
        chatWidth={chatWidth}
        key={`${keyPrefix}-${chatItem.id}`}
        id={`chat-item-${index}`}
        compactHorizontalPadding={compactHorizontalPadding}
      >
        <ImportDivider />
      </ChatItemWrapper>
    );
  }

  const applyFirstMessageWrapper = (
    message: React.ReactNode,
    messageId: string
  ) =>
    messageId === firstMessageId && wrapFirstMessage ? (
      <React.Fragment key={`${keyPrefix}-${messageId}`}>
        {wrapFirstMessage(message)}
      </React.Fragment>
    ) : (
      message
    );

  if (chatItem.type === 'assistantResponse') {
    const chatMessage = (
      <ChatMessage
        chatWidth={chatWidth}
        key={`${keyPrefix}-${chatItem.message.id}`}
        id={`chat-item-${index}`}
        threadId={threadId}
        message={chatItem.message}
        index={chatItem.index}
        isCompact={isCompact}
        compactHorizontalPadding={compactHorizontalPadding}
        isLastAssistantMessage={chatItem.isLastInResponseSequence}
        isNewestResponse={chatItem.isNewestResponse}
        isLive={isLive}
        followsAssistantResponse={chatItem.followsAssistantResponse}
        onAskUserComplete={onAskUserComplete}
      />
    );
    // With a slot, every reply row is a fragment, so the row that gains the
    // slot when the first turn ends keeps its element type and does not remount
    return applyFirstMessageWrapper(
      afterFirstResponse ? (
        <React.Fragment key={`${keyPrefix}-${chatItem.message.id}`}>
          {chatMessage}
          {chatItem.message.id === firstResponseEndId && afterFirstResponse}
        </React.Fragment>
      ) : (
        chatMessage
      ),
      chatItem.message.id
    );
  }

  if (chatItem.type === 'signInConflict') {
    return (
      <ChatItemWrapper
        chatWidth={chatWidth}
        key={`${keyPrefix}-${chatItem.id}`}
        id={`chat-item-${index}`}
        compactHorizontalPadding={compactHorizontalPadding}
      >
        <div className={isCompact ? 'px-1' : 'px-7'}>
          <SignInConflictCard
            conflict={chatItem.conflict}
            threadId={threadId}
          />
        </div>
      </ChatItemWrapper>
    );
  }

  if (chatItem.type === 'reasoning') {
    return (
      <ChatItemWrapper
        chatWidth={chatWidth}
        key={`${keyPrefix}-${chatItem.id}`}
        id={`chat-item-${index}`}
        compactHorizontalPadding={compactHorizontalPadding}
      >
        <ReasoningPanel
          id={chatItem.id}
          steps={chatItem.steps}
          status={chatItem.status}
          isCompact={isCompact}
          forceExpanded={forceExpanded}
        />
      </ChatItemWrapper>
    );
  }

  if (chatItem.type === 'turnActivity') {
    return (
      <ChatItemWrapper
        chatWidth={chatWidth}
        key={`${keyPrefix}-${chatItem.id}`}
        id={`chat-item-${index}`}
        compactHorizontalPadding={compactHorizontalPadding}
      >
        <TurnActivityPanel
          id={chatItem.id}
          items={chatItem.items}
          entries={chatItem.entries}
          isActive={chatItem.isActive}
          isCompact={isCompact}
          forceExpanded={forceExpanded}
          renderItem={(item) =>
            renderTurnActivityChild(item, threadId, isCompact, forceExpanded)
          }
        />
        {chatItem.cardsFromMessageId && (
          <MessageActionCards
            threadId={threadId}
            assistantMessageId={chatItem.cardsFromMessageId}
            isTurnTail
            onAskUserComplete={onAskUserComplete}
          />
        )}
      </ChatItemWrapper>
    );
  }

  if (chatItem.type === 'thinking') {
    return (
      <ChatItemWrapper
        chatWidth={chatWidth}
        key={`${keyPrefix}-${chatItem.id}`}
        id={`chat-item-${index}`}
        compactHorizontalPadding={compactHorizontalPadding}
      >
        <ThinkingIndicator id={chatItem.id} isCompact={isCompact} />
      </ChatItemWrapper>
    );
  }

  if (chatItem.type === 'compactionBanner') {
    return (
      <ChatItemWrapper
        chatWidth={chatWidth}
        key={`${keyPrefix}-${chatItem.id}`}
        id={`chat-item-${index}`}
        compactHorizontalPadding={compactHorizontalPadding}
      >
        <CompactionBanner
          status={chatItem.status}
          threadId={threadId}
          summary={chatItem.summary}
        />
      </ChatItemWrapper>
    );
  }

  if (chatItem.type === 'turnCapHitBanner') {
    return (
      <ChatItemWrapper
        chatWidth={chatWidth}
        key={`${keyPrefix}-${chatItem.id}`}
        id={`chat-item-${index}`}
        compactHorizontalPadding={compactHorizontalPadding}
      >
        <TurnCapHitBanner />
      </ChatItemWrapper>
    );
  }

  if (chatItem.type === 'imageGenQuotaBadge') {
    return (
      <ChatItemWrapper
        chatWidth={chatWidth}
        key={`${keyPrefix}-${chatItem.id}`}
        id={`chat-item-${index}`}
        compactHorizontalPadding={compactHorizontalPadding}
      >
        <ImageGenQuotaBadge
          messageLimit={chatItem.messageLimit}
          nextAllowedTime={chatItem.nextAllowedTime}
          userErrorMessage={chatItem.userErrorMessage}
        />
      </ChatItemWrapper>
    );
  }

  if (chatItem.type === 'pendingTurnCards') {
    return (
      <ChatItemWrapper
        chatWidth={chatWidth}
        key={`${keyPrefix}-${chatItem.id}`}
        id={`chat-item-${index}`}
        compactHorizontalPadding={compactHorizontalPadding}
      >
        <MessageActionCards
          threadId={chatItem.threadId}
          assistantMessageId={chatItem.assistantMessageId}
          isTurnTail
        />
      </ChatItemWrapper>
    );
  }

  return applyFirstMessageWrapper(
    // The note stays in the messages passed to `buildChatItems`, so it still ends the previous turn
    isSystemNote(chatItem.message) && !showSystemNotes ? (
      <div
        key={`${keyPrefix}-${chatItem.message.id}`}
        id={`chat-item-${index}`}
      />
    ) : (
      <ChatMessage
        chatWidth={chatWidth}
        key={`${keyPrefix}-${chatItem.message.id}`}
        id={`chat-item-${index}`}
        threadId={threadId}
        message={chatItem.message}
        index={chatItem.index}
        isCompact={isCompact}
        compactHorizontalPadding={compactHorizontalPadding}
        isLive={isLive}
        onAskUserComplete={onAskUserComplete}
      />
    ),
    chatItem.message.id
  );
}

interface DateSeparatorProps {
  date: string;
}

function DateSeparator({ date }: DateSeparatorProps) {
  return (
    <XStack center className="px-6 h-20 items-center justify-center">
      <Text className="text-[0.8125rem] text-foreground-muted tracking-wider select-none">
        {date}
      </Text>
    </XStack>
  );
}

function ImportDivider() {
  return (
    <XStack center className="h-20 items-center gap-4">
      <Separator className="flex-1" />
      <Text className="text-[0.8125rem] text-foreground-muted tracking-wider select-none">
        Messages above were imported from ChatGPT
      </Text>
      <Separator className="flex-1" />
    </XStack>
  );
}

/**
 * Formats a date for chat message section headers:
 * - Today: "Today"
 * - Yesterday: "Yesterday"
 * - This year: "Sunday, Oct 5"
 * - Other years: "Sunday, Oct 5, 2023"
 */
function formatChatDateSection(date: Date | string): string {
  if (!date) return '';

  const messageDate = new Date(date);

  if (isNaN(messageDate.getTime())) return '';

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (messageDate.toDateString() === today.toDateString()) return 'Today';

  if (messageDate.toDateString() === yesterday.toDateString())
    return 'Yesterday';

  if (messageDate.getFullYear() === today.getFullYear())
    return format(messageDate, 'EEEE, MMM d');

  // Different year - show day of week, month, day and year
  return format(messageDate, 'EEEE, MMM d, yyyy');
}

export interface BuildChatItemsOptions {
  startIndex?: number;
  compactionBanners?: ThreadCompactionBadgeState[];
  turnCapHits?: ThreadTurnCapHitState[];
  imageGenQuotaBadges?: ThreadImageGenQuotaBadgeState[];
  answersInTables?: ReadonlySet<string>;
  /** Surface model thinking in the reasoning panel. Internal users only. */
  showThinking?: boolean;
  /** When false, hide the model's thinking/thought text from the reasoning panel. */
  showThoughts?: boolean;
  isTailWindow?: boolean;
}

const getCompactionBannerKey = (
  compactionBanner: ThreadCompactionBadgeState
): string =>
  `${compactionBanner.afterUserMessageId ?? 'pending'}:${compactionBanner.status}`;

const createTurnCapHitBannerItem = (
  capHit: ThreadTurnCapHitState
): TurnCapHitBannerItem => ({
  type: 'turnCapHitBanner',
  id: `turn-cap-hit-${capHit.assistantMessageId}`,
});

const createImageGenQuotaBadgeItem = (
  badge: ThreadImageGenQuotaBadgeState
): ImageGenQuotaBadgeItem => ({
  type: 'imageGenQuotaBadge',
  id: `image-gen-quota-${badge.afterUserMessageId}`,
  messageLimit: badge.messageLimit,
  nextAllowedTime: badge.nextAllowedTime,
  userErrorMessage: badge.userErrorMessage,
});

const createCompactionBannerItem = (
  compactionBanner: ThreadCompactionBadgeState
): CompactionBannerItem => ({
  type: 'compactionBanner',
  id: `compaction-banner-${getCompactionBannerKey(compactionBanner)}`,
  status: compactionBanner.status,
  summary: compactionBanner.summary,
});

// Stable id derived from the first step so a reasoning panel keeps its React
// identity when ownership transfers between visibles (e.g. trailing-on-prev →
// inherited-on-next as the next visible starts streaming). Same id across the
// move = same DOM node = no unmount/remount.
const getReasoningPanelId = (steps: ReasoningStep[]): string =>
  `reasoning-${steps[0]?.id ?? 'empty'}`;

export const buildChatItems = (
  messages: TMessage[],
  thread?: Thread,
  options?: BuildChatItemsOptions
): ChatItem[] => {
  if (!messages.length) return [];

  const startIndex = options?.startIndex ?? 0;
  const compactionBanners = options?.compactionBanners ?? [];
  const answersInTables = options?.answersInTables ?? EMPTY_GROUP_ANSWER_IDS;
  const turnCapHitsByAssistantMessageId = new Map<
    string,
    ThreadTurnCapHitState
  >();
  (options?.turnCapHits ?? []).forEach((capHit) => {
    turnCapHitsByAssistantMessageId.set(capHit.assistantMessageId, capHit);
  });
  const { displayMessages, reasoningData, standaloneReasoningItems } =
    buildReasoningDisplayItems(messages, thread, {
      filterDisplayableModalities: (
        modalities: ReasoningModalityLike[] | null | undefined
      ) =>
        filterDisplayableModalities(
          modalities as Modality[] | null | undefined
        ) as ReasoningModalityLike[],
      showThinking: options?.showThinking,
      showThoughts: options?.showThoughts,
    });

  // Image-gen quota badges anchor to a user message id (same scheme as
  // chat_compaction). The actual assistant message that relays the limit
  // doesn't exist yet at the time we emit the notification, so we group by
  // the driving user message and attach the badge to the next visible
  // assistant response after that user message.
  const imageGenQuotaBadgesByAnchorUserMessageId = new Map<
    string,
    ThreadImageGenQuotaBadgeState[]
  >();
  (options?.imageGenQuotaBadges ?? []).forEach((badge) => {
    if (!badge.afterUserMessageId) return;
    const list =
      imageGenQuotaBadgesByAnchorUserMessageId.get(badge.afterUserMessageId) ??
      [];
    list.push(badge);
    imageGenQuotaBadgesByAnchorUserMessageId.set(
      badge.afterUserMessageId,
      list
    );
  });

  const items: ChatItem[] = [];
  let pendingCompactionBannersForNextAssistantResponse: ThreadCompactionBadgeState[] =
    [];
  let pendingImageGenQuotaBadgesForNextAssistantResponse: ThreadImageGenQuotaBadgeState[] =
    [];
  const insertedCompactionBannerKeys = new Set<string>();

  let lastDateStr: string | null = null;
  const today = new Date().toDateString();
  const allToday = messages.every(
    (msg) =>
      answersInTables.has(msg.id) ||
      new Date(msg.createdAt).toDateString() === today
  );
  const compactionBannersByAnchorMessageId = new Map<
    string,
    ThreadCompactionBadgeState[]
  >();

  compactionBanners.forEach((compactionBanner) => {
    if (!compactionBanner.afterUserMessageId) {
      return;
    }

    const bannersForAnchor =
      compactionBannersByAnchorMessageId.get(
        compactionBanner.afterUserMessageId
      ) ?? [];
    bannersForAnchor.push(compactionBanner);
    compactionBannersByAnchorMessageId.set(
      compactionBanner.afterUserMessageId,
      bannersForAnchor
    );
  });

  // step.id → tool call lookup (shared with mobile). One tool call per
  // matching modality so duplicate same-name calls bind in order.
  const toolCallByStepId = buildToolCallByStepIdMap(
    messages,
    (modalities) =>
      filterDisplayableModalities(
        modalities as Modality[] | null | undefined
      ) as ReasoningModalityLike[]
  ) as Map<string, ApiToolCallModel>;

  // Single pass: attach tool calls to modality steps and collect the set
  // of contributing messageIds (used by appendPendingApprovals to anchor
  // approval cards to hidden-only messages whose steps got merged onto a
  // later visible reply).
  const enrichReasoningSteps = (
    steps: ReasoningStep[]
  ): { steps: ReasoningStep[]; messageIds: string[] } => {
    const messageIds = new Set<string>();
    if (toolCallByStepId.size === 0) {
      for (const step of steps) {
        if (step.messageId) messageIds.add(step.messageId);
      }
      return { steps, messageIds: Array.from(messageIds) };
    }
    const enriched = steps.map((step) => {
      if (step.messageId) messageIds.add(step.messageId);
      if (step.type !== ReasoningStepType.Modality) return step;
      const tc = toolCallByStepId.get(step.id);
      return tc ? { ...step, toolCall: tc } : step;
    });
    return { steps: enriched, messageIds: Array.from(messageIds) };
  };

  const pushStandaloneReasoningItem = (
    reasoningItem: (typeof standaloneReasoningItems)[number]
  ) => {
    if (reasoningItem.steps.length === 0) return;
    const { steps, messageIds } = enrichReasoningSteps(
      reasoningItem.steps as ReasoningStep[]
    );
    items.push({
      id: getReasoningPanelId(steps),
      type: 'reasoning',
      steps,
      status: reasoningItem.status as ReasoningStatus,
      messageIds,
    });
  };

  const standaloneReasoningItemsByBeforeMessageId = new Map<
    string,
    typeof standaloneReasoningItems
  >();
  const trailingStandaloneReasoningItems: typeof standaloneReasoningItems = [];
  standaloneReasoningItems.forEach((reasoningItem) => {
    if (!reasoningItem.beforeMessageId) {
      trailingStandaloneReasoningItems.push(reasoningItem);
      return;
    }

    const itemsBeforeMessage =
      standaloneReasoningItemsByBeforeMessageId.get(
        reasoningItem.beforeMessageId
      ) ?? [];
    itemsBeforeMessage.push(reasoningItem);
    standaloneReasoningItemsByBeforeMessageId.set(
      reasoningItem.beforeMessageId,
      itemsBeforeMessage
    );
  });

  messages.forEach((message, index) => {
    standaloneReasoningItemsByBeforeMessageId
      .get(message.id)
      ?.forEach(pushStandaloneReasoningItem);

    const messageIndex = startIndex + index;
    const answerInTable = answersInTables.has(message.id);
    if (!answerInTable && !allToday) {
      const messageDate = new Date(message.createdAt);
      const dateStr = messageDate.toDateString();

      if (dateStr !== lastDateStr) {
        items.push({
          type: 'dateSeparator',
          date: formatChatDateSection(messageDate),
          id: `date-${dateStr}`,
        });
      }
      lastDateStr = dateStr;
    }

    if (message.role === 'user') {
      if (!answerInTable) {
        items.push({
          type: 'userMessage',
          message,
          index: messageIndex,
        });
      }

      // A message the user sent into the running turn adds to the turn's pending banners and
      // badges instead of replacing them
      const opensNewTurn = opensTurn(message);
      pendingCompactionBannersForNextAssistantResponse = [
        ...(opensNewTurn
          ? []
          : pendingCompactionBannersForNextAssistantResponse),
        ...(compactionBannersByAnchorMessageId.get(message.id) ?? []),
      ];
      pendingImageGenQuotaBadgesForNextAssistantResponse = [
        ...(opensNewTurn
          ? []
          : pendingImageGenQuotaBadgesForNextAssistantResponse),
        ...(imageGenQuotaBadgesByAnchorUserMessageId.get(message.id) ?? []),
      ];
    }

    if (message.role === 'assistant') {
      if (pendingCompactionBannersForNextAssistantResponse.length > 0) {
        pendingCompactionBannersForNextAssistantResponse.forEach(
          (compactionBanner) => {
            items.push(createCompactionBannerItem(compactionBanner));
            insertedCompactionBannerKeys.add(
              getCompactionBannerKey(compactionBanner)
            );
          }
        );
        pendingCompactionBannersForNextAssistantResponse = [];
      }

      const hasVisibleContent =
        message.content?.trim() ||
        (message.fileIds && message.fileIds.length > 0);

      if (hasVisibleContent) {
        const reasoningEntry = reasoningData.get(message.id);
        if (reasoningEntry && reasoningEntry.inheritedSteps.length > 0) {
          const { steps, messageIds } = enrichReasoningSteps(
            reasoningEntry.inheritedSteps as ReasoningStep[]
          );
          // Inherited steps preceded the visible bubble — by the time we're
          // rendering this assistant row, that earlier work is finished. Hold
          // status to Complete so a search done before the reply doesn't
          // animate as "Searching…" while the reply streams.
          //
          // Panel id is derived from the steps' content so the React identity
          // survives the ownership transition (prev visible's ownSteps → this
          // visible's inheritedSteps) — same DOM node across the move, no
          // unmount/remount that the user sees as a pop-in.
          items.push({
            id: getReasoningPanelId(steps),
            type: 'reasoning',
            steps,
            status: ReasoningStatus.Complete,
            messageIds,
          });
        }
        items.push({
          type: 'assistantResponse',
          message,
          index: messageIndex,
          isLastInResponseSequence: false,
          isNewestResponse: false,
          followsAssistantResponse:
            items[items.length - 1]?.type === 'assistantResponse',
        });

        const capHit = turnCapHitsByAssistantMessageId.get(message.id);
        if (capHit) {
          items.push(createTurnCapHitBannerItem(capHit));
        }

        if (pendingImageGenQuotaBadgesForNextAssistantResponse.length > 0) {
          pendingImageGenQuotaBadgesForNextAssistantResponse.forEach(
            (badge) => {
              items.push(createImageGenQuotaBadgeItem(badge));
            }
          );
          pendingImageGenQuotaBadgesForNextAssistantResponse = [];
        }

        if (reasoningEntry && reasoningEntry.ownSteps.length > 0) {
          const { steps, messageIds } = enrichReasoningSteps(
            reasoningEntry.ownSteps as ReasoningStep[]
          );
          items.push({
            id: getReasoningPanelId(steps),
            type: 'reasoning',
            steps,
            status: reasoningEntry.status as ReasoningStatus,
            messageIds,
          });
        }
      }
    }

    // Imported messages are the first run of the chat, so one divider marks
    // where they end, whichever role the last imported message has
    if (!answerInTable && message.id === thread?.lastImportedMessageId) {
      items.push({ type: 'importDivider', id: `import-divider-${message.id}` });
    }
  });

  trailingStandaloneReasoningItems.forEach(pushStandaloneReasoningItem);

  // Mark assistant responses that are last in their response sequence using
  // the shared helper (last reply before each user message, plus the trailing
  // reply when the thread is not busy).
  const lastInSeqIds = computeLastInResponseSequenceIds(displayMessages, {
    isBusy: thread ? isChatStatusBusy(thread.status) : false,
  });
  // A message refused as busy never reached the server, so the reply before it stays the newest
  // response, which keeps that reply's questions answerable
  const newestMessage = findLast(
    displayMessages,
    (message) => !(message.role === 'user' && message.refusedAsBusy)
  );
  const newestResponseId =
    (options?.isTailWindow ?? true) && newestMessage?.role === 'assistant'
      ? newestMessage.id
      : null;
  for (const item of items) {
    if (item.type === 'assistantResponse') {
      item.isLastInResponseSequence = lastInSeqIds.has(item.message.id);
      item.isNewestResponse = item.message.id === newestResponseId;
    }
  }

  // Inject thinking indicator before assistant content arrives.
  // Two cases: (1) thread is Thinking (waiting for first token), or
  // (2) thread is still NotSubmitted but the user message is already in the list
  //     (message appears before initializeThreadWithMessage sets status to Thinking).
  const lastItem = items[items.length - 1];
  const isThinking = thread?.status === ChatStatus.Thinking;
  const isPendingSubmit =
    thread?.status === ChatStatus.NotSubmitted &&
    lastItem?.type === 'userMessage';
  const alreadyHandled =
    lastItem?.type === 'reasoning' || lastItem?.type === 'thinking';
  const needsLoadingIndicator =
    (isThinking || isPendingSubmit) && !alreadyHandled;

  if (needsLoadingIndicator) {
    pendingCompactionBannersForNextAssistantResponse
      .filter(
        (compactionBanner) =>
          !insertedCompactionBannerKeys.has(
            getCompactionBannerKey(compactionBanner)
          )
      )
      .forEach((compactionBanner) => {
        items.push(createCompactionBannerItem(compactionBanner));
      });
  }

  if (needsLoadingIndicator) {
    items.push({
      id: `thinking-${thread!.id}`,
      type: 'thinking',
    });
  }

  if (thread?.status === ChatStatus.WaitingForClientTools) {
    const lastAssistant = findLast(messages, (m) => m.role === 'assistant');
    if (lastAssistant) {
      items.push({
        type: 'pendingTurnCards',
        id: `pending-turn-cards-${thread.id}`,
        threadId: thread.id,
        assistantMessageId: lastAssistant.id,
      });
    }
  }

  return assignReasoningStatusByPosition(
    insertSignInConflicts(coalesceAdjacentReasoning(items), thread?.id),
    thread
  );
};

const signInConflictOf = (step: ReasoningStep, threadId?: string) => {
  if (step.type !== ReasoningStepType.Modality) return null;
  if (step.toolCall?.tool_name !== 'browser_sign_in_start') return null;
  const conflict = parseSignInConflict(step.toolCall.result);
  return conflict && conflict.chat_id !== threadId ? conflict : null;
};

// Attach one card per blocking session, after the agent's last attempt against it, so retries
// against the same busy sign-in do not stack identical cards
const insertSignInConflicts = (
  items: ChatItem[],
  threadId?: string
): ChatItem[] => {
  const lastStepBySession = new Map<string, string>();
  for (const item of items) {
    if (item.type !== 'reasoning') continue;
    for (const step of item.steps) {
      const conflict = signInConflictOf(step, threadId);
      if (conflict) lastStepBySession.set(conflict.sign_in_session_id, step.id);
    }
  }
  return items.flatMap((item): ChatItem[] => {
    if (item.type !== 'reasoning') return [item];
    const result: ChatItem[] = [];
    let start = 0;
    item.steps.forEach((step, index) => {
      const conflict = signInConflictOf(step, threadId);
      if (!conflict) return;
      if (lastStepBySession.get(conflict.sign_in_session_id) !== step.id)
        return;
      const steps = item.steps.slice(start, index + 1);
      result.push(
        { ...item, id: getReasoningPanelId(steps), steps },
        { type: 'signInConflict', id: `sign-in-conflict-${step.id}`, conflict }
      );
      start = index + 1;
    });
    if (start < item.steps.length) {
      const steps = item.steps.slice(start);
      result.push({ ...item, id: getReasoningPanelId(steps), steps });
    }
    return result;
  });
};

// Adjacent reasoning items in the chat list are conceptually one panel —
// they only got split because the tools straddled a message ownership
// boundary (one visible's ownSteps + the next visible's inheritedSteps).
// Content (a user message or assistantResponse bubble) is the delimiter; any
// stretch of reasoning items with no content between them is one logical
// group. Merge them so the user sees one pill, not two.
const coalesceAdjacentReasoning = (items: ChatItem[]): ChatItem[] => {
  const result: ChatItem[] = [];
  for (const item of items) {
    const prev = result[result.length - 1];
    if (item.type === 'reasoning' && prev && prev.type === 'reasoning') {
      const mergedMessageIds = Array.from(
        new Set([...prev.messageIds, ...item.messageIds])
      );
      result[result.length - 1] = {
        ...prev,
        steps: [...prev.steps, ...item.steps],
        messageIds: mergedMessageIds,
      };
      continue;
    }
    result.push(item);
  }
  return result;
};

// A reasoning panel reads as "still working" when content has yet to arrive
// after it. Past tense only kicks in once a user-facing item (a user message
// or visible assistant bubble) lands in the list AFTER the panel — even if
// the tool itself has technically completed. This gives the user a sense of
// continued activity in the gap between tool-done and the model's reply
// starting to stream.
const assignReasoningStatusByPosition = (
  items: ChatItem[],
  thread?: Thread
): ChatItem[] => {
  const isThreadActive =
    thread?.status === ChatStatus.Thinking ||
    thread?.status === ChatStatus.Streaming ||
    thread?.status === ChatStatus.WaitingForClientTools;
  let hasContentAfter = false;
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    if (item.type === 'userMessage' || item.type === 'assistantResponse') {
      hasContentAfter = true;
      continue;
    }
    if (item.type !== 'reasoning') continue;
    items[i] = {
      ...item,
      status:
        !hasContentAfter && isThreadActive
          ? ReasoningStatus.Pending
          : ReasoningStatus.Complete,
    };
  }
  return items;
};
